import { Box, Button, Checkbox, Flex, Group, Text, Textarea } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { MAX_CERTIFICATION_TEXT_LENGTH } from '@dealstack/shared/lib/common/business/kycCertification';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { PublicCertificationInfo } from './usePublicSigningInfo';
import { useCertificationTextDraft } from './useCertificationTextDraft';

interface Props {
	certification: PublicCertificationInfo;
	isUpdating: boolean;
	hasUpdateError: boolean;
	onUpdate: (text: string) => void;
	onContinue: () => void;
	onEditDetails: () => void;
}

/**
 * DC-825: the KYC certifier reviews the wording stamped beside their signature, amends it if
 * they need to, and confirms they compared the copy with the original. An amendment has to be
 * applied to the document before they can go on to sign it.
 */
export const CertificationTextStep = ({
	certification,
	isUpdating,
	hasUpdateError,
	onUpdate,
	onContinue,
	onEditDetails,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const {
		draft,
		setDraft,
		resetToDefault,
		isDirty,
		isDefault,
		isValid,
		isConfirmed,
		setIsConfirmed,
	} = useCertificationTextDraft(
		certification.certificationText,
		certification.defaultCertificationText
	);

	return (
		<Flex
			direction="column"
			gap="1rem"
			p="1.5rem"
			data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_TEXT_STEP}
		>
			<Box>
				<Text fw={600} size="lg">
					{tCommonUi('eid_easy_signing.certification.text.title', {
						defaultValue: 'Check the certification wording',
					})}
				</Text>
				<Text size="sm" c="dimmed">
					{tCommonUi('eid_easy_signing.certification.text.subtitle', {
						defaultValue:
							'This wording is added to the bottom of the last page, next to your signature. You can amend it before you sign.',
					})}
				</Text>
			</Box>

			<Textarea
				size="md"
				minRows={4}
				autosize
				value={draft}
				onChange={(event) => setDraft(event.currentTarget.value)}
				maxLength={MAX_CERTIFICATION_TEXT_LENGTH}
				disabled={isUpdating}
				label={tCommonUi('eid_easy_signing.certification.text.label', {
					defaultValue: 'Certification wording',
				})}
				description={`${draft.trim().length}/${MAX_CERTIFICATION_TEXT_LENGTH}`}
				error={
					isValid
						? undefined
						: tCommonUi('eid_easy_signing.certification.text.required', {
								defaultValue: 'Enter the certification wording',
							})
				}
				data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_TEXT_INPUT}
			/>

			<Group justify="space-between">
				<Button
					variant="subtle"
					onClick={resetToDefault}
					disabled={isDefault || isUpdating}
					data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_TEXT_RESET}
				>
					{tCommonUi('eid_easy_signing.certification.text.reset', {
						defaultValue: 'Reset to default',
					})}
				</Button>
				<Button
					variant="default"
					onClick={() => onUpdate(draft.trim())}
					loading={isUpdating}
					disabled={!isDirty || !isValid || isUpdating}
					data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_TEXT_UPDATE}
				>
					{tCommonUi('eid_easy_signing.certification.text.update', {
						defaultValue: 'Update document',
					})}
				</Button>
			</Group>

			{hasUpdateError && (
				<Text c="red" size="sm">
					{tCommonUi('eid_easy_signing.certification.text.update_error', {
						defaultValue:
							'We could not update the document. Reload the page and try again.',
					})}
				</Text>
			)}
			{isDirty && !hasUpdateError && (
				<Text size="sm" c="dimmed">
					{tCommonUi('eid_easy_signing.certification.text.unsaved', {
						defaultValue: 'Update the document to see your wording before you sign.',
					})}
				</Text>
			)}

			<Checkbox
				checked={isConfirmed}
				onChange={(event) => setIsConfirmed(event.currentTarget.checked)}
				label={tCommonUi('eid_easy_signing.certification.confirm', {
					defaultValue:
						'I have compared this copy with the original document and the wording above is correct.',
				})}
				data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_CONFIRM}
			/>

			<Button
				fullWidth
				variant="primary"
				onClick={onContinue}
				disabled={!isConfirmed || isDirty || isUpdating}
				data-testid={TEST_ELEMENT_IDS.PUBLIC_SIGNING_CERTIFICATION_CONTINUE}
			>
				{tCommonUi('eid_easy_signing.certification.continue', {
					defaultValue: 'Continue to sign',
				})}
			</Button>
			<Button fullWidth variant="subtle" onClick={onEditDetails} disabled={isUpdating}>
				{tCommonUi('eid_easy_signing.certification.edit_details', {
					defaultValue: 'Change my details',
				})}
			</Button>
		</Flex>
	);
};
